import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Sparkles,
  Bot,
  CheckCircle2,
  Copy,
  Check,
  Clock,
  ArrowRight,
  Users,
  MessageSquare,
  Lightbulb,
  Sliders,
  Send,
  Loader2,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  generateAIConversationSummary,
  generateAISmartReplyAndTonePolisher,
  type AIConversationSummaryResult,
  type AISmartReplyResult,
} from '@/services/aiAssistantService';

interface AIMessagesAdvisorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activeChatTitle?: string;
  initialSnippet?: string;
  onApplyReply?: (replyText: string) => void;
}

export const AIMessagesAdvisorDialog: React.FC<AIMessagesAdvisorDialogProps> = ({
  open,
  onOpenChange,
  activeChatTitle = 'Direct Message',
  initialSnippet = '',
  onApplyReply,
}) => {
  const [activeTab, setActiveTab] = useState<'recap' | 'smart_reply'>('recap');

  // Tab 1: Thread Recap
  const [chatTitle, setChatTitle] = useState(activeChatTitle);
  const [transcriptSnippet, setTranscriptSnippet] = useState(initialSnippet);
  const [focusArea, setFocusArea] = useState<'all' | 'action_items' | 'decisions' | 'sentiment'>('all');
  const [isAnalyzingRecap, setIsAnalyzingRecap] = useState(false);
  const [recapResult, setRecapResult] = useState<AIConversationSummaryResult | null>(null);

  // Tab 2: Smart Reply
  const [incomingContext, setIncomingContext] = useState(initialSnippet);
  const [myDraftIntent, setMyDraftIntent] = useState('');
  const [relationship, setRelationship] = useState<'colleague' | 'friend' | 'business_partner' | 'customer' | 'general'>('colleague');
  const [isGeneratingReplies, setIsGeneratingReplies] = useState(false);
  const [replyResult, setReplyResult] = useState<AISmartReplyResult | null>(null);

  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Sync incoming props when dialog opens or chat changes
  useEffect(() => {
    if (open) {
      if (activeChatTitle) setChatTitle(activeChatTitle);
      if (initialSnippet) {
        setTranscriptSnippet(initialSnippet);
        setIncomingContext(initialSnippet);
      }
    }
  }, [open, activeChatTitle, initialSnippet]);

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleAnalyzeRecap = async () => {
    setIsAnalyzingRecap(true);
    try {
      const res = await generateAIConversationSummary({
        chatTitle: chatTitle || 'Conversation',
        transcriptOrSnippet: transcriptSnippet || 'General coordination discussion regarding milestones, assets, and project deadlines.',
        focusArea,
      });
      setRecapResult(res);
    } catch (err) {
      console.error('Failed to generate AI recap:', err);
    } finally {
      setIsAnalyzingRecap(false);
    }
  };

  const handleGenerateSmartReplies = async () => {
    setIsGeneratingReplies(true);
    try {
      const res = await generateAISmartReplyAndTonePolisher({
        chatTitle: chatTitle || 'Contact',
        incomingContext: incomingContext || 'Hey, could you share an update on the progress and next milestones when you get a chance?',
        myDraftOrIntent: myDraftIntent || undefined,
        relationshipContext: relationship,
      });
      setReplyResult(res);
    } catch (err) {
      console.error('Failed to generate smart replies:', err);
    } finally {
      setIsGeneratingReplies(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white/95 dark:bg-slate-950/95 backdrop-blur-xl border border-blue-100 dark:border-slate-800 shadow-2xl p-4 sm:p-6">
        <DialogHeader className="pb-3 border-b border-gray-100 dark:border-slate-800">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-gradient-to-tr from-blue-600 to-indigo-500 text-white shadow-md shadow-blue-500/20">
              <Bot className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-xl font-bold bg-gradient-to-r from-blue-600 via-indigo-600 to-cyan-500 bg-clip-text text-transparent">
                DeepSeek AI Communication Copilot
              </DialogTitle>
              <DialogDescription className="text-xs sm:text-sm text-gray-500 dark:text-slate-400">
                Conversation Intelligence, Thread Summaries & High-EQ Smart Replies
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="w-full mt-2">
          <TabsList className="grid grid-cols-2 bg-slate-100 dark:bg-slate-900 p-1 rounded-xl">
            <TabsTrigger
              value="recap"
              className="rounded-lg font-medium text-xs sm:text-sm data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800 data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400 transition-all flex items-center gap-2"
            >
              <MessageSquare className="w-4 h-4" />
              Recap & Action Items
            </TabsTrigger>
            <TabsTrigger
              value="smart_reply"
              className="rounded-lg font-medium text-xs sm:text-sm data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800 data-[state=active]:text-indigo-600 dark:data-[state=active]:text-indigo-400 transition-all flex items-center gap-2"
            >
              <Sparkles className="w-4 h-4" />
              Smart Replies & Tone
            </TabsTrigger>
          </TabsList>

          {/* TAB 1: THREAD RECAP */}
          <TabsContent value="recap" className="mt-4 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Chat / Contact Name
                </label>
                <Input
                  value={chatTitle}
                  onChange={(e) => setChatTitle(e.target.value)}
                  placeholder="e.g. Sarah Jenkins or Core Architecture Group"
                  className="text-xs sm:text-sm dark:bg-slate-900 border-slate-200 dark:border-slate-800"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Analysis Focus
                </label>
                <div className="grid grid-cols-4 gap-1">
                  {(['all', 'action_items', 'decisions', 'sentiment'] as const).map((mode) => (
                    <Button
                      key={mode}
                      type="button"
                      variant={focusArea === mode ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setFocusArea(mode)}
                      className="text-[11px] h-9 px-1 capitalize"
                    >
                      {mode.replace('_', ' ')}
                    </Button>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                  Recent Messages or Discussion Transcript
                </label>
                <span className="text-[11px] text-slate-400">Paste snippet or auto-inspect</span>
              </div>
              <Textarea
                rows={3}
                value={transcriptSnippet}
                onChange={(e) => setTranscriptSnippet(e.target.value)}
                placeholder="Paste recent messages from the thread, or let DeepSeek evaluate the discussion highlights..."
                className="text-xs sm:text-sm font-mono dark:bg-slate-900 border-slate-200 dark:border-slate-800"
              />
            </div>

            <Button
              onClick={handleAnalyzeRecap}
              disabled={isAnalyzingRecap}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium py-2 rounded-xl shadow-md transition-all flex items-center justify-center gap-2"
            >
              {isAnalyzingRecap ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Analyzing Conversation Dynamics...
                </>
              ) : (
                <>
                  <Bot className="w-4 h-4" />
                  Generate Executive Recap & Action Items
                </>
              )}
            </Button>

            <AnimatePresence>
              {recapResult && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-4 pt-2"
                >
                  {/* Executive Summary */}
                  <div className="p-4 rounded-xl bg-gradient-to-br from-blue-50/80 to-indigo-50/60 dark:from-blue-950/30 dark:to-indigo-950/20 border border-blue-200 dark:border-blue-900/50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-blue-700 dark:text-blue-300 flex items-center gap-1.5">
                        <Sparkles className="w-3.5 h-3.5" />
                        Executive Summary
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(recapResult.executiveSummary, 'summary')}
                        className="h-7 px-2 text-xs text-blue-600 dark:text-blue-400 hover:bg-blue-100/50 dark:hover:bg-blue-900/30"
                      >
                        {copiedId === 'summary' ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5 text-slate-500" />}
                      </Button>
                    </div>
                    <p className="text-xs sm:text-sm text-slate-800 dark:text-slate-200 leading-relaxed">
                      {recapResult.executiveSummary}
                    </p>
                  </div>

                  {/* Sentiment & Urgency Metrics */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                    <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800">
                      <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 block mb-1">
                        Thread Sentiment
                      </span>
                      <p className="text-xs font-bold text-slate-900 dark:text-slate-100">
                        {recapResult.sentimentTone.overallTone}
                      </p>
                    </div>
                    <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">
                          Cohesion Score
                        </span>
                        <span className="text-xs font-bold text-blue-600 dark:text-blue-400">
                          {recapResult.sentimentTone.cohesionScore}%
                        </span>
                      </div>
                      <div className="w-full bg-slate-200 dark:bg-slate-800 h-1.5 rounded-full overflow-hidden">
                        <div
                          className="bg-blue-500 h-full rounded-full transition-all duration-500"
                          style={{ width: `${recapResult.sentimentTone.cohesionScore}%` }}
                        />
                      </div>
                    </div>
                    <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800">
                      <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 block mb-1">
                        Urgency Level
                      </span>
                      <Badge
                        variant="secondary"
                        className={
                          recapResult.sentimentTone.urgencyLevel === 'High' || recapResult.sentimentTone.urgencyLevel === 'Critical'
                            ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                            : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                        }
                      >
                        {recapResult.sentimentTone.urgencyLevel}
                      </Badge>
                    </div>
                  </div>

                  {/* Key Decisions */}
                  {recapResult.keyDecisions?.length > 0 && (
                    <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 space-y-2">
                      <span className="text-xs font-bold text-slate-900 dark:text-slate-100 flex items-center gap-1.5">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        Key Decisions Reached
                      </span>
                      <ul className="space-y-1.5">
                        {recapResult.keyDecisions.map((decision, idx) => (
                          <li key={idx} className="text-xs text-slate-700 dark:text-slate-300 flex items-start gap-2">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" />
                            <span>{decision}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Action Items */}
                  {recapResult.actionItems?.length > 0 && (
                    <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 space-y-2.5">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-900 dark:text-slate-100 flex items-center gap-1.5">
                          <Sliders className="w-4 h-4 text-indigo-500" />
                          Action Items & Deliverables ({recapResult.actionItems.length})
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            copyToClipboard(
                              recapResult.actionItems.map((a) => `[${a.priority.toUpperCase()}] ${a.task} (@${a.assignee})`).join('\n'),
                              'all-actions'
                            )
                          }
                          className="h-6 px-2 text-[11px] text-slate-600 dark:text-slate-300"
                        >
                          {copiedId === 'all-actions' ? 'Copied' : 'Copy All'}
                        </Button>
                      </div>
                      <div className="space-y-2">
                        {recapResult.actionItems.map((item, idx) => (
                          <div
                            key={idx}
                            className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 p-2.5 rounded-lg bg-white dark:bg-slate-950 border border-slate-200/80 dark:border-slate-800"
                          >
                            <div className="flex items-start gap-2 min-w-0 flex-1">
                              <Badge
                                variant="outline"
                                className={`text-[10px] uppercase font-bold flex-shrink-0 ${
                                  item.priority === 'high'
                                    ? 'border-red-300 text-red-600 bg-red-50 dark:bg-red-950/30'
                                    : item.priority === 'medium'
                                    ? 'border-amber-300 text-amber-600 bg-amber-50 dark:bg-amber-950/30'
                                    : 'border-slate-300 text-slate-600'
                                }`}
                              >
                                {item.priority}
                              </Badge>
                              <span className="text-xs font-medium text-slate-800 dark:text-slate-200">
                                {item.task}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-[11px] text-slate-500 dark:text-slate-400 pl-6 sm:pl-0">
                              <span className="inline-flex items-center gap-1">
                                <Users className="w-3 h-3 text-slate-400" />
                                {item.assignee}
                              </span>
                              {item.suggestedDeadline && (
                                <span className="inline-flex items-center gap-1 text-indigo-600 dark:text-indigo-400 font-medium">
                                  <Clock className="w-3 h-3" />
                                  {item.suggestedDeadline}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recommended Next Steps */}
                  {recapResult.recommendedNextSteps?.length > 0 && (
                    <div className="p-3 rounded-xl bg-amber-50/50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/40">
                      <span className="text-xs font-bold text-amber-900 dark:text-amber-200 flex items-center gap-1.5 mb-1.5">
                        <Lightbulb className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                        Next Recommended Protocol
                      </span>
                      <ul className="space-y-1">
                        {recapResult.recommendedNextSteps.map((step, idx) => (
                          <li key={idx} className="text-xs text-amber-800 dark:text-amber-300 flex items-start gap-1.5">
                            <ArrowRight className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
                            <span>{step}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </TabsContent>

          {/* TAB 2: SMART REPLIES */}
          <TabsContent value="smart_reply" className="mt-4 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Recipient Context
                </label>
                <Input
                  value={chatTitle}
                  onChange={(e) => setChatTitle(e.target.value)}
                  placeholder="e.g. Alex Rivera"
                  className="text-xs sm:text-sm dark:bg-slate-900 border-slate-200 dark:border-slate-800"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Relationship Dynamics
                </label>
                <div className="grid grid-cols-3 gap-1">
                  {[
                    { id: 'colleague', label: 'Colleague' },
                    { id: 'business_partner', label: 'Partner' },
                    { id: 'friend', label: 'Friend' },
                    { id: 'customer', label: 'Client' },
                    { id: 'general', label: 'General' },
                  ].map((rel) => (
                    <Button
                      key={rel.id}
                      type="button"
                      variant={relationship === rel.id ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setRelationship(rel.id as any)}
                      className="text-[11px] h-9 px-1"
                    >
                      {rel.label}
                    </Button>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                Incoming Message to Answer
              </label>
              <Textarea
                rows={2}
                value={incomingContext}
                onChange={(e) => setIncomingContext(e.target.value)}
                placeholder="Paste the message you received..."
                className="text-xs sm:text-sm dark:bg-slate-900 border-slate-200 dark:border-slate-800"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                Your Rough Thoughts or Intent (Optional)
              </label>
              <Input
                value={myDraftIntent}
                onChange={(e) => setMyDraftIntent(e.target.value)}
                placeholder="e.g. Say yes, but ask for 2 extra days to verify test data..."
                className="text-xs sm:text-sm dark:bg-slate-900 border-slate-200 dark:border-slate-800"
              />
            </div>

            <Button
              onClick={handleGenerateSmartReplies}
              disabled={isGeneratingReplies}
              className="w-full bg-gradient-to-r from-indigo-600 to-cyan-600 hover:from-indigo-700 hover:to-cyan-700 text-white font-medium py-2 rounded-xl shadow-md transition-all flex items-center justify-center gap-2"
            >
              {isGeneratingReplies ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Formulating High-EQ Smart Replies...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Generate 5-Tone Smart Replies
                </>
              )}
            </Button>

            <AnimatePresence>
              {replyResult && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-3.5 pt-2"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-900 dark:text-slate-100 flex items-center gap-1.5">
                      <Send className="w-4 h-4 text-indigo-500" />
                      Select Strategic Response Tone
                    </span>
                    <span className="text-[11px] text-slate-500 dark:text-slate-400">
                      1-click copy or insert
                    </span>
                  </div>

                  <div className="space-y-2.5">
                    {replyResult.smartReplies.map((reply, idx) => (
                      <div
                        key={idx}
                        className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 space-y-2 hover:border-indigo-300 dark:hover:border-indigo-800 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <Badge
                            variant="secondary"
                            className="bg-indigo-100 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300 font-semibold text-[11px]"
                          >
                            {reply.tone}
                          </Badge>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyToClipboard(reply.replyText, `reply-${idx}`)}
                              className="h-7 px-2 text-xs text-slate-600 dark:text-slate-300 hover:bg-slate-200/60 dark:hover:bg-slate-800"
                            >
                              {copiedId === `reply-${idx}` ? (
                                <span className="flex items-center gap-1 text-emerald-600 font-medium">
                                  <Check className="w-3.5 h-3.5" /> Copied
                                </span>
                              ) : (
                                <span className="flex items-center gap-1">
                                  <Copy className="w-3.5 h-3.5" /> Copy
                                </span>
                              )}
                            </Button>
                            {onApplyReply && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  onApplyReply(reply.replyText);
                                  onOpenChange(false);
                                }}
                                className="h-7 px-2 text-xs border-indigo-200 dark:border-indigo-800 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/30"
                              >
                                Use in Chat
                              </Button>
                            )}
                          </div>
                        </div>

                        <p className="text-xs sm:text-sm text-slate-800 dark:text-slate-200 leading-relaxed font-sans">
                          &ldquo;{reply.replyText}&rdquo;
                        </p>

                        <p className="text-[11px] text-slate-500 dark:text-slate-400 italic">
                          Insight: {reply.contextBenefit}
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* Timing & Communication Strategy */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                    <div className="p-3 rounded-xl bg-blue-50/60 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900/40">
                      <span className="text-xs font-bold text-blue-900 dark:text-blue-200 flex items-center gap-1.5 mb-1">
                        <Clock className="w-3.5 h-3.5 text-blue-600" />
                        Response Cadence
                      </span>
                      <p className="text-xs text-blue-800 dark:text-blue-300">
                        {replyResult.recommendedFollowUpTiming}
                      </p>
                    </div>

                    <div className="p-3 rounded-xl bg-emerald-50/60 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/40">
                      <span className="text-xs font-bold text-emerald-900 dark:text-emerald-200 flex items-center gap-1.5 mb-1">
                        <Lightbulb className="w-3.5 h-3.5 text-emerald-600" />
                        High-EQ Communication Tip
                      </span>
                      <p className="text-xs text-emerald-800 dark:text-emerald-300">
                        {replyResult.communicationTips?.[0] || 'Keep next steps single and definitive.'}
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

